import { RefObject, useCallback, useMemo, useRef, useState } from "react";
import { Toast } from "primereact/toast";
import { SelectButton } from "primereact/selectbutton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { TabView, TabPanel } from "primereact/tabview";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import "primereact/resources/primereact.min.css";
import * as api from "../../utils/Api.ts";
import { showMsg } from "../../utils/Constants.tsx";
import ChartCard from "../../components/widgets/graphs/ChartCard.tsx";
import KpiCard from "../../components/widgets/graphs/KpiCard.tsx";
import MultiBarChart from "../../components/widgets/graphs/MultiBarChart.tsx";
import ComboBarLineChart from "../../components/widgets/graphs/ComboBarLineChart.tsx";
import DoughnutChart from "../../components/widgets/graphs/DoughnutChart.tsx";
import {
  CHART_COLORS,
  formatMoney,
  formatMoneyCompact,
  formatNumber,
  formatPercent,
} from "../../components/widgets/graphs/chartTheme.ts";
import ReportesHeader from "../../components/reportes/ReportesHeader.tsx";
import EstadosBoxes, {
  EstadoBox,
  MantencionLink,
} from "../../components/reportes/EstadosBoxes.tsx";
import CicloVidaCard, {
  CICLO_ORDEN,
  codigosCiclo,
} from "../../components/reportes/CicloVidaCard.tsx";
import TrazabilidadCard from "../../components/reportes/TrazabilidadCard.tsx";
import SubroganciaSection, {
  Subrogancia,
  SubroganciaResumen,
  subroganciaSheets,
} from "../../components/reportes/SubroganciaSection.tsx";
import { veSubroganciaCompleta } from "../../components/reportes/accesoReportes.ts";
import {
  Count,
  FilterLink,
  Money,
  PctBar,
} from "../../components/reportes/ReportesCells.tsx";
import {
  AGRUPACION_OPTIONS,
  EstadisticaDias,
  EstadosOrden,
  FiltrosReporte,
  InetSeverity,
  ORDENAR_POR_OPTIONS,
  TiemposCiclo,
  clean,
  estadoAprobacionColor,
  exportSheets,
  filtrosSheet,
  formatDias,
  formatFecha,
  formatPeriodo,
  inetInfo,
  normalizarEstadosOrden,
  useCatalogos,
  useFiltrosReporte,
  useReporte,
  useScrollbarGutter,
} from "../../components/reportes/reportesUtils.ts";

/* ------------------------------------------------------------------ */
/* Types (shape returned by /reports/*)                                 */
/* ------------------------------------------------------------------ */

interface Resumen {
  ordenes: {
    cantidadOrdenes: number;
    ordenesVigentes: number;
    ordenesPendientes: number;
    ordenesPendientesGerencia: number;
    ordenesRechazadas: number;
    ordenesAnuladas: number;
    cantidadProveedores: number;
    montoOrdenesVigentesClp: number;
    montoOrdenesPendientesClp: number;
    ticketPromedioClp: number;
  };
  recepciones: {
    ordenesRecepcionadas: number;
    ordenesSinRecepcion: number;
    porcentajeRecepcionado: number;
  };
  facturas: {
    cantidadFacturas: number;
    ordenesFacturadas: number;
    montoFacturado: number;
    porcentajeOrdenesFacturadas: number;
  };
}

interface EstadoRow {
  idEstado: number;
  codigo: string;
  nombre: string;
  cantidad: number;
  montoClp: number;
  cantidadProveedores: number;
  porcentaje: number;
}


interface PeriodoRow {
  periodo: string;
  cantidadOrdenes: number;
  cantidadProveedores: number;
  montoTotalClp: number;
  montoVigenteClp: number;
  pendientes: number;
  anuladas: number;
  rechazadas: number;
  aprobadas: number;
  confirmadas: number;
  pendientesGerencia: number;
  // Not sent yet by /reports/ordenes/periodos (Confirmada GG is currently
  // counted inside "confirmadas"); shown as its own series once present
  confirmadasGerencia?: number;
}

interface CentroCostoRow {
  idCentroCosto: number;
  codigo: string;
  nombre: string;
  area: string;
  cantidadOrdenes: number;
  cantidadLineas: number;
  cantidadProveedores: number;
  cantidadItems: number;
  montoVigenteClp: number;
  montoEnAprobacionClp: number;
  montoDescartadoClp: number;
  participacionGasto: number;
}

interface ProveedorRow {
  idProveedor: number;
  nombre: string;
  rut: string;
  cantidadOrdenes: number;
  ordenesVigentes: number;
  montoVigenteClp: number;
  ticketPromedioClp: number;
  ordenMayorClp: number;
  cantidadMonedas: number;
  participacionGasto: number;
  primeraOrden: string;
  ultimaOrden: string;
}

interface ItemRow {
  idItem: number;
  nombre: string;
  tipoItem: string;
  cantidadLineas: number;
  cantidadOrdenes: number;
  cantidadComprada: number;
  montoClp: number;
  precioUnitarioMinClp: number;
  precioUnitarioMaxClp: number;
  precioUnitarioPromedioClp: number;
  variacionPrecio: number;
  cantidadProveedores: number;
  cantidadCentrosCosto: number;
}

interface MonedaRow {
  idMoneda: number;
  codigo: string;
  nombre: string;
  cantidadOrdenes: number;
  montoOriginal: number;
  montoClp: number;
  tasaPromedio: number;
  participacion: number;
}

interface CondicionPagoRow {
  idCondicion: number;
  nombre: string;
  cantidadOrdenes: number;
  cantidadProveedores: number;
  montoClp: number;
  participacion: number;
}

interface AprobadorRow {
  idUsuario: number;
  usuario: string;
  acciones: number;
  ordenes: number;
  aprobaciones: number;
  confirmaciones: number;
  rechazos: number;
  anulaciones: number;
  tasaRechazo: number;
  promedioDiasDesdeCreacion: number | null;
  ultimaAccion: string;
}

interface RecepcionesResumen {
  kpis: {
    ordenesVigentes: number;
    ordenesConRecepcion: number;
    ordenesSinRecepcion: number;
    porcentajeConRecepcion: number;
    cantidadRecepciones: number;
    recepcionesPorOrden: number;
    montoOrdenesConRecepcionClp: number;
    montoOrdenesSinRecepcionClp: number;
  };
  diasOrdenARecepcion: EstadisticaDias;
  diasEmisionARecepcion: EstadisticaDias;
  antiguedadSinRecepcion: {
    tramo: string;
    cantidadOrdenes: number;
    montoClp: number;
  }[];
  // Current state in ordenes_compra_consolidado_contable (INET)
  porEstadoInet: { estadoInet: number | null; cantidadOrdenes: number }[];
}

interface InetRow {
  estadoInet: number | null;
  label: string;
  severity: InetSeverity;
  cantidad: number;
  porcentaje: number;
}

interface RecepcionPendienteRow {
  nroOrdenCompra: number;
  fechaOrden: string;
  fechaEmision: string | null;
  idEstado: number;
  estado: string;
  idProveedor: number;
  proveedor: string;
  idUsuario: number;
  usuario: string;
  centroCostoPrincipal: string;
  montoClp: number;
  diasPendiente: number;
}

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */

const RANKING_LIMIT = 50;
const RANKING_CHART = 10;
const DETALLE_LIMIT = 200;
const APROBADORES_LIMIT = 20;

const PERIODO_ESTADOS: {
  key: keyof PeriodoRow;
  label: string;
  codigo: string;
}[] = [
  { key: "confirmadas", label: "Confirmadas", codigo: "CONF" },
  { key: "confirmadasGerencia", label: "Confirmadas GG", codigo: "CFGTE" },
  { key: "aprobadas", label: "Aprobadas", codigo: "APRO" },
  { key: "pendientes", label: "Pendientes", codigo: "PEND" },
  { key: "pendientesGerencia", label: "Pendientes GG", codigo: "PDGTE" },
  { key: "rechazadas", label: "Rechazadas", codigo: "RECH" },
  { key: "anuladas", label: "Anuladas", codigo: "ANUL" },
];

// Life-cycle stages exported to Excel (same ones the card shows)
const TRAMOS_ORDEN = codigosCiclo(CICLO_ORDEN);

const ANTIGUEDAD_COLORS = [
  "#1aa37a",
  "#8cc152",
  "#f0a202",
  "#ef7d32",
  "#e0524a",
];

const diasSeverity = (d: number) =>
  d > 90 ? "danger" : d > 30 ? "warning" : "success";

// Big-number boxes (partial states excluded, like the legacy index)
const ESTADOS_BOXES_ORDEN: EstadoBox[] = [
  { codigo: "PEND", titulo: "Pendientes", estado: "Pendiente" },
  { codigo: "ANUL", titulo: "Anuladas", estado: "Anulada" },
  { codigo: "RECH", titulo: "Rechazadas", estado: "Rechazada" },
  { codigo: "APRO", titulo: "Aprobadas", estado: "Aprobada" },
  { codigo: "CONF", titulo: "Confirmadas", estado: "Confirmada" },
  { codigo: "PDGTE", titulo: "Pendientes GG", estado: "Pendiente GG" },
  { codigo: "CFGTE", titulo: "Confirmadas GG", estado: "Confirmada GG" },
];

// Same destination as the legacy NumberBox links
const MANTENCION_LINK: MantencionLink = {
  ruta: "ordenes-compra/mantencion",
  paramEstado: "ocest_nombre",
  paramFecha: "oc_dt_cre",
};

/* ------------------------------------------------------------------ */
/* Screen                                                               */
/* ------------------------------------------------------------------ */

export default function DashboardOrdenesCompra() {
  const toast = useRef<Toast>(null);
  const detalleRef = useRef<HTMLDivElement>(null);

  // Users without cost center 29 also see the OC generated from solicitudes
  // they created in other cost centers (backend alcanceSolicitante)
  const filtros = useFiltrosReporte({ incluirSolicitudesPropias: true });
  const { query, queryKey } = filtros;
  const catalogos = useCatalogos();
  useScrollbarGutter();

  const [agrupacion, setAgrupacion] = useState("mes");
  const [ordenCC, setOrdenCC] = useState("monto");
  const [ordenProveedores, setOrdenProveedores] = useState("monto");
  const [detalleTab, setDetalleTab] = useState(0);
  // Detail tabs load on first visit and stay mounted afterwards
  const [tabsVisitados, setTabsVisitados] = useState<number[]>([0]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [exporting, setExporting] = useState(false);

  const R = api.GET_REPORTES;
  // Presupuesto inside resumen is by calendar year: use the "hasta" year
  const anio = Number(
    (query.fechaHasta ?? "").substring(0, 4) || new Date().getFullYear(),
  );

  const resumen = useReporte<Resumen>(
    () => R.getResumen({ ...query, anio }),
    queryKey,
  );
  const periodos = useReporte<PeriodoRow[]>(
    () => R.getOrdenesPeriodos({ ...query, agrupacion }),
    `${queryKey}|${agrupacion}`,
  );
  const estados = useReporte<EstadosOrden<EstadoRow>>(
    () =>
      R.getOrdenesEstados(query).then((raw: unknown) =>
        normalizarEstadosOrden<EstadoRow>(raw),
      ),
    queryKey,
  );
  const centros = useReporte<CentroCostoRow[]>(
    () =>
      R.getOrdenesCentrosCosto({
        ...query,
        limit: RANKING_LIMIT,
        ordenarPor: ordenCC,
      }),
    `${queryKey}|${ordenCC}`,
  );
  const proveedores = useReporte<ProveedorRow[]>(
    () =>
      R.getOrdenesProveedores({
        ...query,
        limit: RANKING_LIMIT,
        ordenarPor: ordenProveedores,
      }),
    `${queryKey}|${ordenProveedores}`,
  );
  const monedas = useReporte<MonedaRow[]>(
    () => R.getOrdenesMonedas(query),
    queryKey,
  );
  const condiciones = useReporte<CondicionPagoRow[]>(
    () => R.getOrdenesCondicionesPago(query),
    queryKey,
  );
  const recepciones = useReporte<RecepcionesResumen>(
    () => R.getRecepcionesResumen(query),
    queryKey,
  );
  const tiempos = useReporte<TiemposCiclo>(
    () => R.getCicloTiempos(query),
    queryKey,
  );
  // Subrogancia: every pair only for the full-view users (81, 65); the
  // user's own pairs for everyone (empty when not in usuarios_subrogantes)
  const verTodaSubrogancia = veSubroganciaCompleta(filtros.idUsuarioActual);
  const subrogancia = useReporte<Subrogancia | null>(
    () =>
      verTodaSubrogancia
        ? R.getOrdenesSubrogantes(query)
        : Promise.resolve(null),
    `${queryKey}|${verTodaSubrogancia}`,
  );
  const subroganciaPropia = useReporte<SubroganciaResumen | null>(
    () =>
      filtros.idUsuarioActual
        ? R.getSubrogantesResumen({
            ...query,
            idUser: filtros.idUsuarioActual,
          })
        : Promise.resolve(null),
    `${queryKey}|${filtros.idUsuarioActual}`,
  );

  const reportes = [
    resumen,
    periodos,
    estados,
    centros,
    proveedores,
    monedas,
    condiciones,
    recepciones,
    tiempos,
    subrogancia,
    subroganciaPropia,
  ];
  const anyLoading = reportes.some((rep) => rep.loading);

  const reloadAll = () => {
    reportes.forEach((rep) => rep.reload());
    setRefreshKey((k) => k + 1);
  };

  const openDetalle = (index: number) => {
    setDetalleTab(index);
    setTabsVisitados((v) => (v.includes(index) ? v : [...v, index]));
  };

  const scrollTo = (ref: RefObject<HTMLDivElement>) =>
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  /* ------------------------- derived chart data ------------------------- */

  const periodoChart = useMemo(() => {
    const rows = periodos.data ?? [];
    const conGG = rows.some((r) => typeof r.confirmadasGerencia === "number");
    return {
      labels: rows.map((r) => formatPeriodo(r.periodo)),
      bars: [
        {
          label: "Monto vigente",
          data: rows.map((r) => r.montoVigenteClp),
          color: CHART_COLORS.facturado,
        },
        {
          label: "No vigente (pend./rech./anul.)",
          data: rows.map((r) => r.montoTotalClp - r.montoVigenteClp),
          color: CHART_COLORS.neutral,
        },
      ],
      lines: [
        {
          label: "N° OC",
          data: rows.map((r) => r.cantidadOrdenes),
          color: CHART_COLORS.pendiente,
        },
      ],
      // Until the backend splits Confirmada GG out of "confirmadas", say so
      // in the label instead of drawing an empty GG series
      estados: PERIODO_ESTADOS.filter(
        (e) => e.key !== "confirmadasGerencia" || conGG,
      ).map((e) => ({
        label:
          e.key === "confirmadas" && !conGG
            ? "Confirmadas (incluye GG)"
            : e.label,
        data: rows.map((r) => Number(r[e.key]) || 0),
        color: estadoAprobacionColor(e.codigo),
      })),
    };
  }, [periodos.data]);

  const estadosChart = useMemo(() => {
    const rows = (estados.data?.porOrden ?? []).filter((r) => r.cantidad > 0);
    return {
      rows,
      labels: rows.map((r) => r.nombre),
      values: rows.map((r) => r.cantidad),
      colors: rows.map((r) => estadoAprobacionColor(r.codigo)),
      total: rows.reduce((a, r) => a + r.cantidad, 0),
    };
  }, [estados.data]);

  const monedasChart = useMemo(() => {
    const rows = monedas.data ?? [];
    return {
      labels: rows.map((r) => clean(r.codigo) || clean(r.nombre)),
      values: rows.map((r) => r.montoClp),
      total: rows.reduce((a, r) => a + r.montoClp, 0),
    };
  }, [monedas.data]);

  const condicionesChart = useMemo(() => {
    const rows = (condiciones.data ?? []).slice(0, RANKING_CHART);
    return {
      labels: rows.map((r) => clean(r.nombre)),
      series: [
        {
          label: "Monto OC",
          data: rows.map((r) => r.montoClp),
          color: CHART_COLORS.facturado,
        },
      ],
    };
  }, [condiciones.data]);

  const topCentros = useMemo(
    () => (centros.data ?? []).slice(0, RANKING_CHART),
    [centros.data],
  );

  const centrosChart = useMemo(
    () => ({
      labels: topCentros.map((c) => clean(c.nombre)),
      series:
        ordenCC === "cantidad"
          ? [
              {
                label: "N° OC",
                data: topCentros.map((c) => c.cantidadOrdenes),
                color: CHART_COLORS.facturado,
              },
              {
                label: "Líneas",
                data: topCentros.map((c) => c.cantidadLineas),
                color: CHART_COLORS.neutral,
              },
            ]
          : [
              {
                label: "Vigente",
                data: topCentros.map((c) => c.montoVigenteClp),
                color: CHART_COLORS.facturado,
                stack: "monto",
              },
              {
                label: "En aprobación",
                data: topCentros.map((c) => c.montoEnAprobacionClp),
                color: CHART_COLORS.pendiente,
                stack: "monto",
              },
            ],
    }),
    [topCentros, ordenCC],
  );

  const topProveedores = useMemo(
    () => (proveedores.data ?? []).slice(0, RANKING_CHART),
    [proveedores.data],
  );

  const proveedoresChart = useMemo(
    () => ({
      labels: topProveedores.map((p) => clean(p.nombre) || p.rut),
      series: [
        ordenProveedores === "cantidad"
          ? {
              label: "N° OC",
              data: topProveedores.map((p) => p.cantidadOrdenes),
              color: CHART_COLORS.ordenado,
            }
          : {
              label: "Monto vigente",
              data: topProveedores.map((p) => p.montoVigenteClp),
              color: CHART_COLORS.ordenado,
            },
      ],
    }),
    [topProveedores, ordenProveedores],
  );

  const inetChart = useMemo(() => {
    const data = recepciones.data?.porEstadoInet ?? [];
    const total = data.reduce((a, r) => a + r.cantidadOrdenes, 0);
    const rows: InetRow[] = data
      .filter((r) => r.cantidadOrdenes > 0)
      .map((r) => {
        const info = inetInfo(r.estadoInet);
        return {
          estadoInet: r.estadoInet,
          label: info.label,
          severity: info.severity,
          cantidad: r.cantidadOrdenes,
          porcentaje: total ? (r.cantidadOrdenes / total) * 100 : 0,
        };
      })
      .sort((a, b) => (a.estadoInet ?? 99) - (b.estadoInet ?? 99));
    const cantidad = (estado: number) =>
      rows.find((r) => r.estadoInet === estado)?.cantidad ?? 0;
    return {
      rows,
      total,
      labels: rows.map((r) => r.label),
      values: rows.map((r) => r.cantidad),
      colors: rows.map((r) => inetInfo(r.estadoInet).color),
      cumplidas: cantidad(4),
      parciales: cantidad(3),
      emitidas: cantidad(2),
      pctCumplidas: total ? (cantidad(4) / total) * 100 : 0,
    };
  }, [recepciones.data]);

  // Stable bar-click handlers: inline ones would rebuild the chart options
  // (and replay the animation) on every re-render, e.g. agrupación changes
  const { filterByCentroCosto, filterByProveedor } = filtros;
  const onCentroBarClick = useCallback(
    (index: number) => filterByCentroCosto(topCentros[index]?.idCentroCosto),
    [topCentros, filterByCentroCosto],
  );
  const onProveedorBarClick = useCallback(
    (index: number) => {
      const p = topProveedores[index];
      if (p) filterByProveedor(p.idProveedor, p.nombre);
    },
    [topProveedores, filterByProveedor],
  );
  // Opens the "OC sin recepción" tab (setters and ref are stable)
  const onAntiguedadBarClick = useCallback(() => {
    setDetalleTab(0);
    setTabsVisitados((v) => (v.includes(0) ? v : [...v, 0]));
    detalleRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const antiguedadChart = useMemo(() => {
    const rows = recepciones.data?.antiguedadSinRecepcion ?? [];
    return {
      labels: rows.map((r) => `${r.tramo} días`),
      series: [
        {
          label: "Monto OC sin recepción",
          data: rows.map((r) => r.montoClp),
          color: ANTIGUEDAD_COLORS,
        },
      ],
      total: rows.reduce((a, r) => a + r.cantidadOrdenes, 0),
    };
  }, [recepciones.data]);

  /* ------------------------- excel ------------------------- */

  const exportExcel = async () => {
    setExporting(true);
    try {
      // Detail tabs load lazily: fetch their data now (one request at a time)
      const pendientes: RecepcionPendienteRow[] =
        (await R.getRecepcionesPendientes({
          ...query,
          limit: DETALLE_LIMIT,
        })) ?? [];
      const items: ItemRow[] =
        (await R.getOrdenesItems({
          ...query,
          limit: DETALLE_LIMIT,
          ordenarPor: "monto",
        })) ?? [];
      const aprobadores: AprobadorRow[] =
        (await R.getOrdenesAprobadores({
          ...query,
          limit: APROBADORES_LIMIT,
        })) ?? [];

      const o = resumen.data?.ordenes;
      const rec = resumen.data?.recepciones;
      const fac = resumen.data?.facturas;
      await exportSheets("Reporte_Ordenes_Compra", [
        filtrosSheet(
          filtros.applied,
          catalogos.data,
          filtros.alcance,
          filtros.incluyePropias,
          "OC de sus solicitudes en otros centros",
        ),
        {
          name: "Resumen",
          rows:
            o && rec && fac
              ? [
                  { Indicador: "Órdenes de compra", Valor: o.cantidadOrdenes },
                  { Indicador: "OC vigentes", Valor: o.ordenesVigentes },
                  { Indicador: "OC pendientes", Valor: o.ordenesPendientes },
                  {
                    Indicador: "OC pendientes gerencia",
                    Valor: o.ordenesPendientesGerencia,
                  },
                  { Indicador: "OC rechazadas", Valor: o.ordenesRechazadas },
                  { Indicador: "OC anuladas", Valor: o.ordenesAnuladas },
                  { Indicador: "Proveedores", Valor: o.cantidadProveedores },
                  {
                    Indicador: "Monto vigente CLP",
                    Valor: o.montoOrdenesVigentesClp,
                  },
                  {
                    Indicador: "Monto en aprobación CLP",
                    Valor: o.montoOrdenesPendientesClp,
                  },
                  {
                    Indicador: "Ticket promedio CLP",
                    Valor: o.ticketPromedioClp,
                  },
                  {
                    Indicador: "OC con recepción",
                    Valor: rec.ordenesRecepcionadas,
                  },
                  {
                    Indicador: "OC sin recepción",
                    Valor: rec.ordenesSinRecepcion,
                  },
                  {
                    Indicador: "% recepcionado",
                    Valor: rec.porcentajeRecepcionado,
                  },
                  { Indicador: "OC facturadas", Valor: fac.ordenesFacturadas },
                  {
                    Indicador: "Monto facturado CLP",
                    Valor: fac.montoFacturado,
                  },
                  {
                    Indicador: "% OC facturadas",
                    Valor: fac.porcentajeOrdenesFacturadas,
                  },
                ]
              : [],
        },
        {
          name: "Periodos",
          rows: (periodos.data ?? []).map((p) => ({
            Periodo: p.periodo,
            "N° OC": p.cantidadOrdenes,
            Proveedores: p.cantidadProveedores,
            "Monto total CLP": p.montoTotalClp,
            "Monto vigente CLP": p.montoVigenteClp,
            Confirmadas: p.confirmadas,
            Aprobadas: p.aprobadas,
            Pendientes: p.pendientes,
            "Pendientes GG": p.pendientesGerencia,
            "Confirmadas GG":
              p.confirmadasGerencia ?? "Incluidas en Confirmadas",
            Rechazadas: p.rechazadas,
            Anuladas: p.anuladas,
          })),
        },
        {
          name: "Estado INET",
          rows: inetChart.rows.map((r) => ({
            "Estado INET": r.label,
            Código: r.estadoInet ?? "-",
            "N° OC": r.cantidad,
            "%": Number(r.porcentaje.toFixed(2)),
          })),
        },
        {
          name: "Estados aprobacion",
          rows: (estados.data?.porOrden ?? []).map((e) => ({
            Estado: e.nombre,
            "N° OC": e.cantidad,
            "Monto CLP": e.montoClp,
            Proveedores: e.cantidadProveedores,
            "%": e.porcentaje,
          })),
        },
        {
          name: "Estados linea",
          rows: (estados.data?.porLinea ?? []).map((e) => ({
            Estado: e.nombre,
            "N° lineas": e.cantidad,
            "Monto CLP": e.montoClp,
            "%": e.porcentaje,
          })),
        },
        {
          name: "Centros costo",
          rows: (centros.data ?? []).map((c) => ({
            Código: clean(c.codigo),
            "Centro de costo": clean(c.nombre),
            Área: clean(c.area),
            "N° OC": c.cantidadOrdenes,
            Líneas: c.cantidadLineas,
            Proveedores: c.cantidadProveedores,
            Ítems: c.cantidadItems,
            "Vigente CLP": c.montoVigenteClp,
            "En aprobación CLP": c.montoEnAprobacionClp,
            "Descartado CLP": c.montoDescartadoClp,
            "% del gasto": c.participacionGasto,
          })),
        },
        {
          name: "Proveedores",
          rows: (proveedores.data ?? []).map((p) => ({
            RUT: p.rut,
            Proveedor: clean(p.nombre),
            "N° OC": p.cantidadOrdenes,
            "OC vigentes": p.ordenesVigentes,
            "Vigente CLP": p.montoVigenteClp,
            "Ticket promedio CLP": p.ticketPromedioClp,
            "OC mayor CLP": p.ordenMayorClp,
            "% del gasto": p.participacionGasto,
            "Primera OC": formatFecha(p.primeraOrden),
            "Última OC": formatFecha(p.ultimaOrden),
          })),
        },
        {
          name: "Monedas",
          rows: (monedas.data ?? []).map((m) => ({
            Moneda: clean(m.codigo),
            Nombre: clean(m.nombre),
            "N° OC": m.cantidadOrdenes,
            "Monto original": m.montoOriginal,
            "Monto CLP": m.montoClp,
            "Tasa promedio": m.tasaPromedio,
            "%": m.participacion,
          })),
        },
        {
          name: "Condiciones pago",
          rows: (condiciones.data ?? []).map((c) => ({
            Condición: clean(c.nombre),
            "N° OC": c.cantidadOrdenes,
            Proveedores: c.cantidadProveedores,
            "Monto CLP": c.montoClp,
            "%": c.participacion,
          })),
        },
        {
          name: "Recepciones pendientes",
          rows: pendientes.map((p) => ({
            "N° OC": p.nroOrdenCompra,
            "Fecha OC": formatFecha(p.fechaOrden),
            "Fecha emisión": formatFecha(p.fechaEmision),
            Estado: p.estado,
            Proveedor: clean(p.proveedor),
            Usuario: p.usuario,
            "Centro de costo": clean(p.centroCostoPrincipal),
            "Monto CLP": p.montoClp,
            "Días pendiente": p.diasPendiente,
          })),
        },
        {
          name: "Items",
          rows: items.map((i) => ({
            Ítem: clean(i.nombre),
            "N° OC": i.cantidadOrdenes,
            Líneas: i.cantidadLineas,
            "Cantidad comprada": i.cantidadComprada,
            "Monto CLP": i.montoClp,
            "Precio mín CLP": i.precioUnitarioMinClp,
            "Precio máx CLP": i.precioUnitarioMaxClp,
            "Precio prom CLP": i.precioUnitarioPromedioClp,
            "Variación precio %": i.variacionPrecio,
            Proveedores: i.cantidadProveedores,
            "Centros de costo": i.cantidadCentrosCosto,
          })),
        },
        {
          name: "Aprobadores",
          rows: aprobadores.map((a) => ({
            Usuario: a.usuario,
            Acciones: a.acciones,
            "N° OC": a.ordenes,
            Aprobaciones: a.aprobaciones,
            Confirmaciones: a.confirmaciones,
            Rechazos: a.rechazos,
            Anulaciones: a.anulaciones,
            "% rechazo": a.tasaRechazo,
            "Días promedio desde creación": a.promedioDiasDesdeCreacion,
            "Última acción": formatFecha(a.ultimaAccion),
          })),
        },
        // Same visibility as the screen (see SubroganciaSection)
        ...subroganciaSheets(
          "ordenes",
          verTodaSubrogancia,
          subrogancia.data,
          subroganciaPropia.data,
        ),
        {
          name: "Tiempos ciclo",
          rows: (tiempos.data?.tramos ?? [])
            .filter((t) => TRAMOS_ORDEN.includes(t.codigo))
            .map((t) => ({
              Tramo: t.nombre,
              Casos: t.cantidad,
              "Promedio (días)": t.promedio,
              "Mediana (días)": t.mediana,
              "P90 (días)": t.p90,
              "Mínimo (días)": t.minimo,
              "Máximo (días)": t.maximo,
              Descartados: t.descartados,
            })),
        },
      ]);
    } catch (e) {
      console.error("Error exportando reporte órdenes:", e);
      showMsg(toast, "error", "Error", "No fue posible generar el Excel");
    } finally {
      setExporting(false);
    }
  };

  /* ------------------------- render ------------------------- */

  const o = resumen.data?.ordenes;
  const rec = resumen.data?.recepciones;
  const fac = resumen.data?.facturas;
  const enAprobacion =
    (o?.ordenesPendientes ?? 0) + (o?.ordenesPendientesGerencia ?? 0);
  // Rejected / cancelled totals come from ordenes/estados (resumen has no
  // amounts for them); rechazadas include "Rechazada parcialmente"
  const sumaEstados = (codigos: string[]) =>
    (estados.data?.porOrden ?? [])
      .filter((e) => codigos.includes(clean(e.codigo)))
      .reduce(
        (acc, e) => ({
          cantidad: acc.cantidad + e.cantidad,
          monto: acc.monto + e.montoClp,
        }),
        { cantidad: 0, monto: 0 },
      );
  const rechazadas = sumaEstados(["RECH", "RECHP"]);
  const anuladas = sumaEstados(["ANUL"]);
  const montoDescartado = rechazadas.monto + anuladas.monto;
  const montoTotalEstados = (estados.data?.porOrden ?? []).reduce(
    (a, e) => a + e.montoClp,
    0,
  );
  const recKpis = recepciones.data?.kpis;

  return (
    <div className="fd-dashboard rp-dashboard">
      <Toast ref={toast} />

      <ReportesHeader
        eyebrow="Compras · Órdenes de compra"
        title="Reporte de órdenes de compra"
        icon="pi-shopping-cart"
        filtros={filtros}
        catalogos={catalogos.data}
        toast={toast}
        textoPropias={{
          opcion: "solo OC de tus solicitudes",
          chip: "OC de tus solicitudes",
        }}
        loading={anyLoading}
        exporting={exporting}
        onReload={reloadAll}
        onExport={exportExcel}
      />

      {/* ------------------------- ESTADOS (big numbers) ------------------------- */}
      <div className="rp-estados-pila">
        <EstadosBoxes
          titulo="Documentos OC"
          nota="Cantidad de órdenes por estado · clic para ver en mantención"
          estados={estados.data?.porOrden ?? null}
          boxes={ESTADOS_BOXES_ORDEN}
          link={MANTENCION_LINK}
          fechaDesde={query.fechaDesde}
          fechaHasta={query.fechaHasta}
          loading={estados.loading}
          error={estados.error}
        />
        {/* Shown once ordenes/estados also returns porLinea */}
        {estados.loading || estados.data?.porLinea ? (
          <EstadosBoxes
            titulo="Líneas OC"
            nota="Cantidad de líneas de detalle por estado de la línea"
            estados={estados.data?.porLinea ?? null}
            boxes={ESTADOS_BOXES_ORDEN}
            link={MANTENCION_LINK}
            loading={estados.loading}
            error={estados.error}
            navegable={false}
          />
        ) : null}
      </div>

      {/* ------------------------- KPIs ------------------------- */}
      {resumen.error ? (
        <ChartCard
          title="Resumen"
          error={resumen.error}
          onRetry={resumen.reload}
          height={80}
        />
      ) : (
        <div className="fd-kpis">
          <KpiCard
            title="Órdenes de compra"
            icon="pi-shopping-cart"
            color={CHART_COLORS.ordenado}
            loading={resumen.loading}
            value={formatNumber(o?.cantidadOrdenes)}
            hint={`${formatNumber(o?.ordenesVigentes)} vigentes · ${formatNumber(
              o?.cantidadProveedores,
            )} proveedores`}
          />
          <KpiCard
            title="Monto OC vigentes"
            icon="pi-wallet"
            color={CHART_COLORS.facturado}
            loading={resumen.loading}
            value={formatMoney(o?.montoOrdenesVigentesClp)}
            hint={`Ticket promedio ${formatMoney(o?.ticketPromedioClp)}`}
          />
          <KpiCard
            title="Pendientes"
            icon="pi-hourglass"
            color={CHART_COLORS.pendiente}
            tone={enAprobacion > 0 ? "warning" : "success"}
            loading={resumen.loading}
            value={formatMoney(o?.montoOrdenesPendientesClp)}
            hint={`${formatNumber(o?.ordenesPendientes)} pendientes · ${formatNumber(
              o?.ordenesPendientesGerencia,
            )} pendientes GG`}
          />
          <KpiCard
            title="Rechazadas / anuladas"
            icon="pi-ban"
            color={CHART_COLORS.diferencia}
            tone={montoDescartado > 0 ? "danger" : "success"}
            loading={estados.loading}
            value={estados.error ? "Sin datos" : formatMoney(montoDescartado)}
            progress={
              montoTotalEstados
                ? (montoDescartado / montoTotalEstados) * 100
                : 0
            }
            hint={
              estados.error
                ? "No fue posible cargar los estados"
                : `${formatNumber(rechazadas.cantidad)} rechazadas (${formatMoneyCompact(
                    rechazadas.monto,
                  )}) · ${formatNumber(anuladas.cantidad)} anuladas (${formatMoneyCompact(
                    anuladas.monto,
                  )})`
            }
          />
          <KpiCard
            title="OC con recepción"
            icon="pi-box"
            color={CHART_COLORS.contabilizado}
            loading={resumen.loading}
            value={formatPercent(rec?.porcentajeRecepcionado)}
            progress={rec?.porcentajeRecepcionado ?? 0}
            hint={`${formatNumber(rec?.ordenesSinRecepcion)} OC sin recepción · ver listado`}
            onClick={() => {
              openDetalle(0);
              scrollTo(detalleRef);
            }}
          />
          <KpiCard
            title="OC facturadas"
            icon="pi-file"
            color={CHART_COLORS.facturado}
            loading={resumen.loading}
            value={formatPercent(fac?.porcentajeOrdenesFacturadas)}
            progress={fac?.porcentajeOrdenesFacturadas ?? 0}
            hint={`${formatNumber(fac?.ordenesFacturadas)} OC · ${formatMoney(
              fac?.montoFacturado,
            )} facturado`}
          />
          <KpiCard
            title="OC cumplidas (INET)"
            icon="pi-verified"
            color={CHART_COLORS.contabilizado}
            loading={recepciones.loading}
            value={formatPercent(inetChart.pctCumplidas)}
            progress={inetChart.pctCumplidas}
            hint={`${formatNumber(inetChart.cumplidas)} cumplidas · ${formatNumber(
              inetChart.parciales,
            )} parciales · ${formatNumber(inetChart.emitidas)} emitidas`}
          />
          <KpiCard
            title="Monto facturado"
            icon="pi-receipt"
            color={CHART_COLORS.contabilizado}
            loading={resumen.loading}
            value={formatMoney(fac?.montoFacturado)}
            progress={
              o?.montoOrdenesVigentesClp
                ? ((fac?.montoFacturado ?? 0) / o.montoOrdenesVigentesClp) * 100
                : 0
            }
            hint={`${formatNumber(fac?.cantidadFacturas)} facturas`}
          />
        </div>
      )}

      {/* ------------------------- EVOLUCION ------------------------- */}
      <ChartCard
        title="Evolución de órdenes de compra"
        subtitle="Monto vigente vs no vigente (CLP) y N° de OC"
        loading={periodos.loading}
        error={periodos.error}
        onRetry={periodos.reload}
        empty={!periodos.data?.length}
        actions={
          <SelectButton
            value={agrupacion}
            options={AGRUPACION_OPTIONS}
            onChange={(e) => e.value && setAgrupacion(e.value)}
            className="fd-select-sm"
            allowEmpty={false}
          />
        }
      >
        <ComboBarLineChart
          labels={periodoChart.labels}
          bars={periodoChart.bars}
          lines={periodoChart.lines}
        />
      </ChartCard>

      {/* ------------------------- CICLO DE VIDA ------------------------- */}
      <CicloVidaCard
        title="Ciclo de vida de la orden de compra"
        subtitle="Días por etapa, desde la creación de la OC hasta la contabilización"
        reporte={tiempos}
        definicion={CICLO_ORDEN}
      />

      {/* ------------------------- APROBACION ------------------------- */}
      <div className="rp-section">
        <h5 className="rp-section-title">
          <i className="pi pi-check-square" />
          Aprobación de órdenes de compra
        </h5>
        <span className="rp-section-desc">
          Estado del flujo de aprobación (pendiente → confirmada GG)
        </span>
      </div>
      <div className="fd-grid fd-grid-1-1">
        <ChartCard
          title="Estado de aprobación por período"
          subtitle="N° de OC según su estado de aprobación actual"
          loading={periodos.loading}
          error={periodos.error}
          onRetry={periodos.reload}
          empty={!periodos.data?.length}
        >
          <MultiBarChart
            labels={periodoChart.labels}
            series={periodoChart.estados}
            stacked
            money={false}
          />
        </ChartCard>

        <ChartCard
          title="Estado de aprobación"
          subtitle="Flujo interno: pendiente → aprobada → confirmada / GG"
          loading={estados.loading}
          error={estados.error}
          onRetry={estados.reload}
          empty={!estadosChart.total}
        >
          <div className="fd-split">
            <div className="fd-split-chart">
              <DoughnutChart
                labels={estadosChart.labels}
                values={estadosChart.values}
                colors={estadosChart.colors}
                height={260}
                centerLabel={formatNumber(estadosChart.total)}
                centerCaption="órdenes"
              />
            </div>
            <div className="fd-split-table">
              <DataTable
                value={estadosChart.rows}
                size="small"
                className="fd-table"
              >
                <Column
                  header="Estado"
                  body={(e: EstadoRow) => (
                    <span className="rp-dot-label">
                      <span
                        className="rp-dot"
                        style={{
                          backgroundColor: estadoAprobacionColor(e.codigo),
                        }}
                      />
                      {e.nombre}
                    </span>
                  )}
                />
                <Column
                  header="OC"
                  body={(e: EstadoRow) => <Count value={e.cantidad} />}
                />
                <Column
                  header="Monto"
                  body={(e: EstadoRow) => <Money value={e.montoClp} />}
                />
              </DataTable>
            </div>
          </div>
        </ChartCard>
      </div>

      {/* ------------------------- SUBROGANCIA ------------------------- */}
      <SubroganciaSection
        tipo="ordenes"
        global={subrogancia}
        propia={subroganciaPropia}
        verTodo={verTodaSubrogancia}
        query={query}
        queryKey={queryKey}
      />

      {/* ------------------------- CUMPLIMIENTO INET ------------------------- */}
      <div className="rp-section">
        <h5 className="rp-section-title">
          <i className="pi pi-truck" />
          Cumplimiento INET
        </h5>
        <span className="rp-section-desc">
          Estado actual en el consolidado contable: emitida, parcialmente
          cumplida, cumplida (recepción y pago)
        </span>
      </div>
      <div className="fd-grid fd-grid-1-1">
        <ChartCard
          title="Estado INET de cumplimiento"
          subtitle={
            inetChart.total
              ? `${formatNumber(inetChart.total)} OC registradas en el consolidado contable`
              : "Consolidado contable (ordenes_compra_consolidado_contable)"
          }
          loading={recepciones.loading}
          error={recepciones.error}
          onRetry={recepciones.reload}
          empty={!inetChart.total}
          emptyMessage="Sin OC en el consolidado contable para los filtros seleccionados"
        >
          <div className="fd-split">
            <div className="fd-split-chart">
              <DoughnutChart
                labels={inetChart.labels}
                values={inetChart.values}
                colors={inetChart.colors}
                height={260}
                centerLabel={formatPercent(inetChart.pctCumplidas)}
                centerCaption="cumplidas"
              />
            </div>
            <div className="fd-split-table">
              <DataTable
                value={inetChart.rows}
                size="small"
                className="fd-table"
              >
                <Column
                  header="Estado INET"
                  body={(r: InetRow) => (
                    <Tag value={r.label} severity={r.severity} />
                  )}
                />
                <Column
                  header="OC"
                  body={(r: InetRow) => <Count value={r.cantidad} />}
                />
                <Column
                  header="%"
                  body={(r: InetRow) => (
                    <span className="fd-num">
                      {formatPercent(r.porcentaje)}
                    </span>
                  )}
                />
              </DataTable>
            </div>
          </div>
        </ChartCard>

        <ChartCard
          title="Antigüedad de OC sin recepción"
          subtitle={
            recKpis
              ? `${formatNumber(antiguedadChart.total)} OC · ${formatMoneyCompact(
                  recKpis.montoOrdenesSinRecepcionClp,
                )} · mediana orden → recepción ${formatDias(
                  recepciones.data?.diasOrdenARecepcion?.mediana,
                )}`
              : "Días desde la creación de la OC"
          }
          loading={recepciones.loading}
          error={recepciones.error}
          onRetry={recepciones.reload}
          empty={!antiguedadChart.total}
          emptyMessage="Todas las OC vigentes tienen recepción"
        >
          <MultiBarChart
            labels={antiguedadChart.labels}
            series={antiguedadChart.series}
            showLegend={false}
            maxLabelLength={14}
            onBarClick={onAntiguedadBarClick}
          />
        </ChartCard>
      </div>

      {/* ------------------------- GASTO ------------------------- */}
      <div className="rp-section">
        <h5 className="rp-section-title">
          <i className="pi pi-chart-pie" />
          Gasto
        </h5>
        <span className="rp-section-desc">Dónde y con quién se compra</span>
      </div>
      {/* ------------------------- CENTROS DE COSTO ------------------------- */}
      <div className="fd-grid fd-grid-1-1">
        <ChartCard
          title={`Top ${RANKING_CHART} centros de costo que más gastan`}
          subtitle="Clic en una barra para filtrar el dashboard por ese centro de costo"
          loading={centros.loading}
          error={centros.error}
          onRetry={centros.reload}
          empty={!topCentros.length}
          height={420}
          actions={
            <SelectButton
              value={ordenCC}
              options={ORDENAR_POR_OPTIONS}
              onChange={(e) => e.value && setOrdenCC(e.value)}
              className="fd-select-sm"
              allowEmpty={false}
            />
          }
        >
          <MultiBarChart
            labels={centrosChart.labels}
            series={centrosChart.series}
            horizontal
            stacked={ordenCC !== "cantidad"}
            money={ordenCC !== "cantidad"}
            height={420}
            onBarClick={onCentroBarClick}
          />
        </ChartCard>

        <ChartCard
          title="Detalle por centro de costo"
          subtitle={`Hasta ${RANKING_LIMIT} centros de costo, ordenados por ${ordenCC}`}
          loading={centros.loading}
          error={centros.error}
          onRetry={centros.reload}
          height={420}
        >
          <DataTable
            value={centros.data ?? []}
            size="small"
            scrollable
            scrollHeight="420px"
            sortMode="single"
            removableSort
            stripedRows
            emptyMessage="Sin centros de costo para los filtros seleccionados"
            className="fd-table"
          >
            <Column
              header="Centro de costo"
              field="nombre"
              sortable
              style={{ minWidth: "12rem" }}
              body={(c: CentroCostoRow) => (
                <FilterLink
                  name={c.nombre}
                  sub={c.area}
                  title="Filtrar dashboard por este centro de costo"
                  onClick={() => filtros.filterByCentroCosto(c.idCentroCosto)}
                />
              )}
            />
            <Column
              header="OC"
              field="cantidadOrdenes"
              sortable
              body={(c: CentroCostoRow) => <Count value={c.cantidadOrdenes} />}
            />
            <Column
              header="Vigente"
              field="montoVigenteClp"
              sortable
              body={(c: CentroCostoRow) => <Money value={c.montoVigenteClp} />}
            />
            <Column
              header="En aprob."
              field="montoEnAprobacionClp"
              sortable
              body={(c: CentroCostoRow) => (
                <Money value={c.montoEnAprobacionClp} />
              )}
            />
            <Column
              header="% del gasto"
              field="participacionGasto"
              sortable
              style={{ minWidth: "8rem" }}
              body={(c: CentroCostoRow) => (
                <PctBar value={c.participacionGasto} />
              )}
            />
          </DataTable>
        </ChartCard>
      </div>

      {/* ------------------------- PROVEEDORES ------------------------- */}
      <div className="fd-grid fd-grid-1-1">
        <ChartCard
          title={`Top ${RANKING_CHART} proveedores`}
          subtitle="Clic en una barra para filtrar el dashboard por ese proveedor"
          loading={proveedores.loading}
          error={proveedores.error}
          onRetry={proveedores.reload}
          empty={!topProveedores.length}
          height={420}
          actions={
            <SelectButton
              value={ordenProveedores}
              options={ORDENAR_POR_OPTIONS}
              onChange={(e) => e.value && setOrdenProveedores(e.value)}
              className="fd-select-sm"
              allowEmpty={false}
            />
          }
        >
          <MultiBarChart
            labels={proveedoresChart.labels}
            series={proveedoresChart.series}
            horizontal
            money={ordenProveedores !== "cantidad"}
            showLegend={false}
            height={420}
            onBarClick={onProveedorBarClick}
          />
        </ChartCard>

        <ChartCard
          title="Detalle por proveedor"
          subtitle={`Hasta ${RANKING_LIMIT} proveedores, ordenados por ${ordenProveedores}`}
          loading={proveedores.loading}
          error={proveedores.error}
          onRetry={proveedores.reload}
          height={420}
        >
          <DataTable
            value={proveedores.data ?? []}
            size="small"
            scrollable
            scrollHeight="420px"
            sortMode="single"
            removableSort
            stripedRows
            emptyMessage="Sin proveedores para los filtros seleccionados"
            className="fd-table"
          >
            <Column
              header="Proveedor"
              field="nombre"
              sortable
              style={{ minWidth: "12rem" }}
              body={(p: ProveedorRow) => (
                <FilterLink
                  name={p.nombre}
                  sub={p.rut}
                  title="Filtrar dashboard por este proveedor"
                  onClick={() =>
                    filtros.filterByProveedor(p.idProveedor, p.nombre)
                  }
                />
              )}
            />
            <Column
              header="OC"
              field="cantidadOrdenes"
              sortable
              body={(p: ProveedorRow) => <Count value={p.cantidadOrdenes} />}
            />
            <Column
              header="Vigente"
              field="montoVigenteClp"
              sortable
              body={(p: ProveedorRow) => <Money value={p.montoVigenteClp} />}
            />
            <Column
              header="Ticket prom."
              field="ticketPromedioClp"
              sortable
              body={(p: ProveedorRow) => <Money value={p.ticketPromedioClp} />}
            />
            <Column
              header="% del gasto"
              field="participacionGasto"
              sortable
              style={{ minWidth: "8rem" }}
              body={(p: ProveedorRow) => (
                <PctBar value={p.participacionGasto} />
              )}
            />
          </DataTable>
        </ChartCard>
      </div>
      {/* ------------------------- MONEDAS + CONDICIONES ------------------------- */}
      <div className="fd-grid fd-grid-1-1">
        <ChartCard
          title="OC por moneda"
          subtitle="Monto convertido a CLP"
          loading={monedas.loading}
          error={monedas.error}
          onRetry={monedas.reload}
          empty={!monedas.data?.length}
        >
          <div className="fd-split">
            <div className="fd-split-chart">
              <DoughnutChart
                labels={monedasChart.labels}
                values={monedasChart.values}
                money
                height={260}
                centerLabel={formatMoneyCompact(monedasChart.total)}
                centerCaption="en CLP"
              />
            </div>
            <div className="fd-split-table">
              <DataTable
                value={monedas.data ?? []}
                size="small"
                className="fd-table"
              >
                <Column
                  header="Moneda"
                  body={(m: MonedaRow) => clean(m.codigo) || clean(m.nombre)}
                />
                <Column
                  header="OC"
                  body={(m: MonedaRow) => <Count value={m.cantidadOrdenes} />}
                />
                <Column
                  header="Monto original"
                  body={(m: MonedaRow) => (
                    <span className="fd-num">
                      {m.montoOriginal.toLocaleString("es-CL", {
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  )}
                />
                <Column
                  header="Tasa prom."
                  body={(m: MonedaRow) => (
                    <span className="fd-num">
                      {m.tasaPromedio.toLocaleString("es-CL", {
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  )}
                />
              </DataTable>
            </div>
          </div>
        </ChartCard>

        <ChartCard
          title="Condiciones de pago"
          subtitle={`Top ${RANKING_CHART} por monto OC (CLP)`}
          loading={condiciones.loading}
          error={condiciones.error}
          onRetry={condiciones.reload}
          empty={!condiciones.data?.length}
        >
          <MultiBarChart
            labels={condicionesChart.labels}
            series={condicionesChart.series}
            horizontal
            showLegend={false}
          />
        </ChartCard>
      </div>

      {/* ------------------------- DETALLE ------------------------- */}
      <div ref={detalleRef} className="fd-anchor">
        <ChartCard
          title="Detalle para seguimiento"
          subtitle="Cada listado se carga al abrir su pestaña"
        >
          <TabView
            activeIndex={detalleTab}
            onTabChange={(e) => openDetalle(e.index)}
            renderActiveOnly={false}
            className="fd-tabs"
          >
            <TabPanel header="OC sin recepción">
              {tabsVisitados.includes(0) ? (
                <RecepcionesPendientesTab
                  filtros={filtros}
                  refreshKey={refreshKey}
                />
              ) : null}
            </TabPanel>
            <TabPanel header="Ítems más comprados">
              {tabsVisitados.includes(1) ? (
                <ItemsTab filtros={filtros} refreshKey={refreshKey} />
              ) : null}
            </TabPanel>
            <TabPanel header="Aprobadores">
              {tabsVisitados.includes(2) ? (
                <AprobadoresTab filtros={filtros} refreshKey={refreshKey} />
              ) : null}
            </TabPanel>
            <TabPanel header="Trazabilidad">
              {tabsVisitados.includes(3) ? (
                <TrazabilidadCard
                  filtros={filtros}
                  buscarPor="nroOrdenCompra"
                  refreshKey={refreshKey}
                />
              ) : null}
            </TabPanel>
          </TabView>
        </ChartCard>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Lazy detail tabs                                                     */
/* ------------------------------------------------------------------ */

interface TabProps {
  filtros: FiltrosReporte;
  refreshKey: number;
}

function TabError({ error, onRetry }: { error: string; onRetry: () => void }) {
  return <ChartCard title="" error={error} onRetry={onRetry} height={120} />;
}

function RecepcionesPendientesTab({ filtros, refreshKey }: TabProps) {
  const pendientes = useReporte<RecepcionPendienteRow[]>(
    () =>
      api.GET_REPORTES.getRecepcionesPendientes({
        ...filtros.query,
        limit: DETALLE_LIMIT,
      }),
    `${filtros.queryKey}|${refreshKey}`,
  );

  if (pendientes.error) {
    return <TabError error={pendientes.error} onRetry={pendientes.reload} />;
  }
  return (
    <>
      <div className="fd-note">
        OC vigentes sin ninguna recepción registrada (hasta {DETALLE_LIMIT}, las
        más antiguas primero).
      </div>
      <DataTable
        value={pendientes.data ?? []}
        loading={pendientes.loading}
        size="small"
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sortMode="single"
        removableSort
        stripedRows
        emptyMessage="No hay OC sin recepción para los filtros seleccionados"
        className="fd-table"
      >
        <Column
          header="N° OC"
          field="nroOrdenCompra"
          sortable
          bodyClassName="fd-num"
        />
        <Column
          header="Proveedor"
          field="proveedor"
          sortable
          body={(p: RecepcionPendienteRow) => (
            <FilterLink
              name={p.proveedor}
              sub={p.estado}
              title="Filtrar dashboard por este proveedor"
              onClick={() =>
                filtros.filterByProveedor(p.idProveedor, p.proveedor)
              }
            />
          )}
        />
        <Column
          header="Solicitante"
          field="usuario"
          sortable
          body={(p: RecepcionPendienteRow) => (
            <FilterLink
              name={p.usuario}
              title="Filtrar dashboard por este usuario"
              onClick={() => filtros.filterByUsuario(p.idUsuario, p.usuario)}
            />
          )}
        />
        <Column
          header="Centro costo"
          field="centroCostoPrincipal"
          sortable
          body={(p: RecepcionPendienteRow) =>
            clean(p.centroCostoPrincipal) || "-"
          }
        />
        <Column
          header="Fecha OC"
          field="fechaOrden"
          sortable
          body={(p: RecepcionPendienteRow) => formatFecha(p.fechaOrden)}
        />
        <Column
          header="Emisión"
          field="fechaEmision"
          sortable
          body={(p: RecepcionPendienteRow) => formatFecha(p.fechaEmision)}
        />
        <Column
          header="Antigüedad"
          field="diasPendiente"
          sortable
          body={(p: RecepcionPendienteRow) => (
            <Tag
              value={`${formatNumber(p.diasPendiente)} días`}
              severity={diasSeverity(p.diasPendiente)}
            />
          )}
        />
        <Column
          header="Monto"
          field="montoClp"
          sortable
          body={(p: RecepcionPendienteRow) => <Money value={p.montoClp} />}
        />
      </DataTable>
    </>
  );
}

function ItemsTab({ filtros, refreshKey }: TabProps) {
  const [ordenarPor, setOrdenarPor] = useState("monto");
  const items = useReporte<ItemRow[]>(
    () =>
      api.GET_REPORTES.getOrdenesItems({
        ...filtros.query,
        limit: DETALLE_LIMIT,
        ordenarPor,
      }),
    `${filtros.queryKey}|${ordenarPor}|${refreshKey}`,
  );

  if (items.error) {
    return <TabError error={items.error} onRetry={items.reload} />;
  }
  return (
    <>
      <div className="fd-note rp-search">
        <span>
          Hasta {DETALLE_LIMIT} ítems. Variación = (precio máx − mín) / mín.
        </span>
        <SelectButton
          value={ordenarPor}
          options={ORDENAR_POR_OPTIONS}
          onChange={(e) => e.value && setOrdenarPor(e.value)}
          className="fd-select-sm"
          allowEmpty={false}
        />
      </div>
      <DataTable
        value={items.data ?? []}
        loading={items.loading}
        size="small"
        paginator
        rows={10}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sortMode="single"
        removableSort
        stripedRows
        emptyMessage="Sin ítems para los filtros seleccionados"
        className="fd-table"
      >
        <Column
          header="Ítem"
          field="nombre"
          sortable
          body={(i: ItemRow) => (
            <span className="fd-prov-name">{truncateItem(i.nombre)}</span>
          )}
        />
        <Column
          header="OC"
          field="cantidadOrdenes"
          sortable
          body={(i: ItemRow) => <Count value={i.cantidadOrdenes} />}
        />
        <Column
          header="Cant. comprada"
          field="cantidadComprada"
          sortable
          body={(i: ItemRow) => <Count value={i.cantidadComprada} />}
        />
        <Column
          header="Monto"
          field="montoClp"
          sortable
          body={(i: ItemRow) => <Money value={i.montoClp} />}
        />
        <Column
          header="Precio prom."
          field="precioUnitarioPromedioClp"
          sortable
          body={(i: ItemRow) => <Money value={i.precioUnitarioPromedioClp} />}
        />
        <Column
          header="Mín / Máx"
          body={(i: ItemRow) => (
            <span className="fd-num">
              {formatMoney(i.precioUnitarioMinClp)}
              <span className="fd-sub">
                {" "}
                / {formatMoney(i.precioUnitarioMaxClp)}
              </span>
            </span>
          )}
        />
        <Column
          header="Variación"
          field="variacionPrecio"
          sortable
          body={(i: ItemRow) => (
            <span
              className={`fd-num${i.variacionPrecio >= 50 ? " fd-neg" : ""}`}
            >
              {formatPercent(i.variacionPrecio)}
            </span>
          )}
        />
        <Column
          header="Prov."
          field="cantidadProveedores"
          sortable
          body={(i: ItemRow) => <Count value={i.cantidadProveedores} />}
        />
      </DataTable>
    </>
  );
}

const truncateItem = (nombre: string) => {
  const text = clean(nombre);
  return text.length > 48 ? `${text.substring(0, 47)}…` : text || "-";
};

function AprobadoresTab({ filtros, refreshKey }: TabProps) {
  const aprobadores = useReporte<AprobadorRow[]>(
    () =>
      api.GET_REPORTES.getOrdenesAprobadores({
        ...filtros.query,
        limit: APROBADORES_LIMIT,
      }),
    `${filtros.queryKey}|${refreshKey}`,
  );

  if (aprobadores.error) {
    return <TabError error={aprobadores.error} onRetry={aprobadores.reload} />;
  }
  return (
    <>
      <div className="fd-note">
        Top {APROBADORES_LIMIT} usuarios por acciones de aprobación /
        confirmación de OC.
      </div>
      <DataTable
        value={aprobadores.data ?? []}
        loading={aprobadores.loading}
        size="small"
        sortMode="single"
        removableSort
        stripedRows
        emptyMessage="Sin aprobaciones para los filtros seleccionados"
        className="fd-table"
      >
        <Column
          header="Aprobador"
          field="usuario"
          sortable
          body={(a: AprobadorRow) => (
            <span className="fd-prov-name">{clean(a.usuario)}</span>
          )}
        />
        <Column
          header="OC"
          field="ordenes"
          sortable
          body={(a: AprobadorRow) => <Count value={a.ordenes} />}
        />
        <Column
          header="Aprobaciones"
          field="aprobaciones"
          sortable
          body={(a: AprobadorRow) => <Count value={a.aprobaciones} />}
        />
        <Column
          header="Confirmaciones"
          field="confirmaciones"
          sortable
          body={(a: AprobadorRow) => <Count value={a.confirmaciones} />}
        />
        <Column
          header="Rechazos"
          field="rechazos"
          sortable
          body={(a: AprobadorRow) => <Count value={a.rechazos} />}
        />
        <Column
          header="Anulaciones"
          field="anulaciones"
          sortable
          body={(a: AprobadorRow) => <Count value={a.anulaciones} />}
        />
        <Column
          header="% Rechazo"
          field="tasaRechazo"
          sortable
          body={(a: AprobadorRow) => (
            <span className="fd-num">{formatPercent(a.tasaRechazo)}</span>
          )}
        />
        <Column
          header="Días prom."
          field="promedioDiasDesdeCreacion"
          sortable
          headerTooltip="Promedio de días desde la creación de la OC hasta la acción"
          body={(a: AprobadorRow) => (
            <span className="fd-num">
              {formatDias(a.promedioDiasDesdeCreacion)}
            </span>
          )}
        />
        <Column
          header="Última acción"
          field="ultimaAccion"
          sortable
          body={(a: AprobadorRow) => formatFecha(a.ultimaAccion)}
        />
      </DataTable>
    </>
  );
}
